import { useEffect } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';

const HEARTBEAT_KEY = 'deuna-backstage-tab-heartbeat';
const HEARTBEAT_INTERVAL_MS = 15_000;
const STALE_THRESHOLD_MS = 60_000;

/**
 * Forces sign-out when no tab has been alive for STALE_THRESHOLD_MS.
 *
 * Why: Backstage stores its identity token in localStorage. Combined with
 * `sessionDuration: 15min`, a user who closes the browser is silently let
 * back in on reopen. Security policy (CR-1115) requires the session be
 * invalidated quickly after the browser closes. Backstage's built-in
 * `logoutIfDisconnected` already does this but reuses the 5-min idle
 * threshold, which is too lax.
 *
 * How: any open tab writes a heartbeat to localStorage every 15s
 * (independent of user activity, unlike Backstage's onAction-based
 * `lastSeenOnline`). On mount, if the most recent heartbeat is older than
 * 60s, all tabs were closed and the browser was reopened — sign out.
 *
 * Multi-tab safe: opening a second tab while the first is still alive sees
 * a fresh heartbeat (≤15s old) and does not sign out. Idle-tab safe: the
 * interval keeps writing even if the user is not interacting.
 *
 * Must live OUTSIDE AppRouter so it runs before AppRouter auto-authenticates
 * the stale identity from localStorage.
 */
export const BrowserCloseLogout = () => {
  const identityApi = useApi(identityApiRef);

  useEffect(() => {
    const writeBeat = () => {
      localStorage.setItem(HEARTBEAT_KEY, String(Date.now()));
    };

    const lastBeatStr = localStorage.getItem(HEARTBEAT_KEY);
    const lastBeat = lastBeatStr ? Number(lastBeatStr) : NaN;
    const isStale =
      Number.isFinite(lastBeat) && Date.now() - lastBeat > STALE_THRESHOLD_MS;

    writeBeat();

    if (isStale) {
      identityApi
        .getCredentials()
        .then(({ token }) => {
          if (token) identityApi.signOut();
        })
        .catch(() => {});
    }

    const interval = setInterval(writeBeat, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [identityApi]);

  return null;
};
